import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Button, Avatar } from '@saleshandy/design-system';
import {
  Lock,
  UserList,
  Users,
  World,
  ChevronDown,
  ChevronUp,
  InfoCircle,
  Plus,
  Checks,
  Diamond,
  AddList,
} from '@saleshandy/icons';
import { useDispatch, useSelector } from 'react-redux';
import Spinner, {
  SpinnerAnimation,
} from '../../../../shared/design-system/components/atoms/spinner/spinner';
import Checkbox from '../../../../shared/design-system/components/atoms/checkbox';
import Input from '../../../../shared/design-system/components/input';
import Switch from '../../../../shared/design-system/components/atoms/switch';
import { accessibleOnClick } from '../../../../shared/utils';
import TruncatedTextWithAutoTooltip from '../../../../shared/components/truncated-text-with-auto-tooltip';
import Modal from '../../../../shared/design-system/components/atoms/modal';
import CreateEditSavedListModal from '../filter/saved-list/create-edit-saved-list-modal';
import { Team as TeamType } from '../../../settings/types/users-and-teams';
import { RequestStatus } from '../../../../shared/enums/request-status';
import {
  LeadFinderListVisibility,
  LeadFinderSecondaryTab,
  LeadRevealType,
} from '../../types';
import {
  OverlayTooltip,
  Placement,
} from '../../../../shared/design-system/components/overlay';
import { RootState } from '../../../../store/root-reducer';
import { openCreditNotAvailableModal } from '../../v2-lead-slice';
import {
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
} from '../../../../shared/utils/user-details';
import { AnalyticsEvents } from '../../../../shared/enums/analytics';

export type AddToListData = {
  id: number;
  label: string;
  visibility: LeadFinderListVisibility;
  owner: {
    firstName: string;
    lastName: string;
  };
  isDefault?: boolean;
};

interface AddToListDropdownProps {
  lists: AddToListData[];
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (
    open: boolean,
    options?: { preserveRowSelection?: boolean },
  ) => void;
  onAddToList?: (
    listIds: number[],
    shouldEnrich?: boolean,
    newLists?: Array<{ name: string; visibility: string; teamIds?: number[] }>,
    revealType?: LeadRevealType,
  ) => void | boolean | Promise<void | boolean>;
  onCreateNewList?: () => void;
  disabledListIds?: number[];
  teamsList?: TeamType[];
  getTeamsListRequestStatus?: RequestStatus;
  sendGetTeamsListRequest?: VoidFunction;
  onSaveList?: (
    listId: number | null,
    listName: string,
    isShared: boolean,
    teamIds: number[],
    shouldEnrich?: boolean,
    revealType?: LeadRevealType,
  ) => void;
  isUpdateListLoading?: boolean;
  isCompanyTab?: boolean;
  activeSubTab?: LeadFinderSecondaryTab;
  selectedResourcesCount?: number;
  availableCredits?: number;
}

const AddToListDropdown: React.FC<AddToListDropdownProps> = ({
  lists = [],
  disabled = false,
  open: controlledOpen,
  onOpenChange,
  onAddToList,
  onCreateNewList,
  disabledListIds = [],
  teamsList = [],
  getTeamsListRequestStatus,
  sendGetTeamsListRequest = () => {},
  onSaveList,
  isUpdateListLoading = false,
  isCompanyTab = false,
  activeSubTab,
  selectedResourcesCount = 0,
  availableCredits = 0,
}) => {
  const isPeopleSavedTab =
    !isCompanyTab && activeSubTab === LeadFinderSecondaryTab.SAVED;
  const dispatch = useDispatch();
  const [internalShow, setInternalShow] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedListIds, setSelectedListIds] = useState<number[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const isClosingAddToListForCreateModalRef = useRef(false);
  const shouldOpenCreateModalAfterCloseRef = useRef(false);
  const shouldOpenAddToListAfterCreateCloseRef = useRef(false);
  const [expandedSections, setExpandedSections] = useState<{
    alreadyIn: boolean;
    public: boolean;
    private: boolean;
    teams: boolean;
  }>({
    alreadyIn: true,
    public: true,
    private: true,
    teams: true,
  });
  const [hoveredListId, setHoveredListId] = useState<number | null>(null);
  const [shouldEnrich, setShouldEnrich] = useState(true);
  const [peopleEnrichmentType, setPeopleEnrichmentType] = useState<
    'email' | 'email_phone'
  >('email');

  const leadFinderCredits = useSelector(
    (state: RootState) => state?.home?.leadFinder?.credits,
  );

  const {
    isSelectedAllLeads,
    isSelectedAllCompanies,
    peopleMaxLeadsLimit,
    companyMaxLeadsLimit,
  } = useSelector((state: RootState) => state.leadFinderV2.selection);

  const defaultEmailRevealType = useSelector(
    (state: RootState) => state?.home?.defaultEmailRevealType,
  );

  const isPersonalEmailMode = defaultEmailRevealType === 'personal_email';
  const emailCredit = isPersonalEmailMode ? 2 : leadFinderCredits?.email;
  const emailPhoneCredit = isPersonalEmailMode
    ? 9
    : leadFinderCredits?.emailphone;
  const companyCredit = leadFinderCredits?.company;

  const disabledListIdsSet = useMemo(
    () => new Set<number>((disabledListIds || []).map((id) => Number(id))),
    [disabledListIds],
  );

  const isControlled = controlledOpen !== undefined;
  const show = isControlled ? controlledOpen : internalShow;
  const setShow = (
    value: boolean,
    options?: { preserveRowSelection?: boolean },
  ) => {
    if (isControlled) {
      onOpenChange?.(value, options);
    } else {
      setInternalShow(value);
    }
  };

  // Reset selected lists when modal closes
  useEffect(() => {
    if (!show) {
      if (isClosingAddToListForCreateModalRef.current) {
        isClosingAddToListForCreateModalRef.current = false;
        return;
      }
      setSelectedListIds([]);
      setSearchValue('');
      setShouldEnrich(true);
      setPeopleEnrichmentType('email');
    }
  }, [show]);

  const toggleSection = (
    section: 'alreadyIn' | 'public' | 'private' | 'teams',
  ) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleListSelection = (listId: number) => {
    if (disabledListIdsSet.has(listId)) {
      return;
    }
    setSelectedListIds((prev) => {
      if (prev.includes(listId)) {
        return prev.filter((id) => id !== listId);
      }
      return [...prev, listId];
    });
  };

  const handleSelectAllInSection = (
    checked: boolean,
    sectionLists: AddToListData[],
  ) => {
    const sectionIds = sectionLists
      .map((list) => list.id)
      .filter((id) => !disabledListIdsSet.has(id));
    if (checked) {
      setSelectedListIds((prev) => {
        const newIds = [...prev, ...sectionIds];
        return Array.from(new Set(newIds));
      });
    } else {
      setSelectedListIds((prev) =>
        prev.filter((id) => !sectionIds.includes(id)),
      );
    }
  };

  const isAllSelectedInSection = (sectionLists: AddToListData[]) => {
    const enabledLists = sectionLists.filter(
      (list) => !disabledListIdsSet.has(list.id),
    );
    if (enabledLists.length === 0) return false;
    return enabledLists.every((list) => selectedListIds.includes(list.id));
  };

  const isSomeSelectedInSection = (sectionLists: AddToListData[]) =>
    sectionLists
      .filter((list) => !disabledListIdsSet.has(list.id))
      .some((list) => selectedListIds.includes(list.id)) &&
    !isAllSelectedInSection(sectionLists);

  const handleAdd = async () => {
    if (!onAddToList) return;

    if (isCompanyTab && shouldEnrich) {
      const effectiveCount =
        isSelectedAllCompanies && companyMaxLeadsLimit != null
          ? companyMaxLeadsLimit
          : selectedResourcesCount;
      const required = Number(effectiveCount) * 0.1;
      if (Number(availableCredits) < required) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.Company));
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
    }

    try {
      setIsSaving(true);
      let addResult: boolean | void;
      if (isCompanyTab) {
        addResult = await Promise.resolve(
          onAddToList(selectedListIds, shouldEnrich),
        );
      } else {
        let revealType: LeadRevealType;
        if (peopleEnrichmentType === 'email') {
          revealType = isPersonalEmailMode
            ? LeadRevealType.PersonalEmail
            : LeadRevealType.Email;
        } else {
          revealType = isPersonalEmailMode
            ? LeadRevealType.EmailPersonalEmailPhone
            : LeadRevealType.EmailPhone;
        }
        addResult = await Promise.resolve(
          onAddToList(selectedListIds, undefined, undefined, revealType),
        );
      }
      // Close only on success. Parent returns `false` when the save failed or
      if (addResult !== false) {
        setShow(false);
      }
    } catch (e) {
      // keep modal open so user can retry; parent handler may show error
      // no-op
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleOpenCreateModal = () => {
    isClosingAddToListForCreateModalRef.current = true;
    shouldOpenCreateModalAfterCloseRef.current = true;
    // When controlled, notify parent to preserve row selection while closing modal
    setShow(false, { preserveRowSelection: true });
  };

  // Group lists by visibility and filter by search
  const groupedLists = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    const matchesQuery = (label: string) =>
      query.length === 0 ? true : label.toLowerCase().includes(query);

    // Already In: lists the lead/company is in + default lists (All Companies / All leads) - always show default
    const alreadyInLists = lists.filter(
      (list) =>
        (disabledListIdsSet.has(list.id) || list.isDefault === true) &&
        matchesQuery(list.label),
    );

    // Lists not yet added - exclude already added and default lists
    const publicLists = lists.filter(
      (list) =>
        list.visibility === LeadFinderListVisibility.Public &&
        !disabledListIdsSet.has(list.id) &&
        !list.isDefault &&
        matchesQuery(list.label),
    );
    const privateLists = lists.filter(
      (list) =>
        list.visibility === LeadFinderListVisibility.Private &&
        !disabledListIdsSet.has(list.id) &&
        !list.isDefault &&
        matchesQuery(list.label),
    );
    const teamsLists = lists.filter(
      (list) =>
        list.visibility === LeadFinderListVisibility.Team &&
        !disabledListIdsSet.has(list.id) &&
        !list.isDefault &&
        matchesQuery(list.label),
    );

    return {
      alreadyIn: alreadyInLists,
      public: publicLists,
      private: privateLists,
      teams: teamsLists,
    };
  }, [lists, searchValue, disabledListIds]);

  const renderAlreadyInSection = (sectionLists: AddToListData[]) => {
    if (sectionLists.length === 0) return null;

    const isExpanded = expandedSections.alreadyIn;

    return (
      <div
        key="alreadyIn"
        className="add-to-list-dropdown__section add-to-list-dropdown__section--already-in"
      >
        <div
          className={`add-to-list-dropdown__section-header ${
            isExpanded ? 'add-to-list-dropdown__section-header--expanded' : ''
          }`}
        >
          <div
            className="add-to-list-dropdown__section-header-left"
            {...accessibleOnClick(() => toggleSection('alreadyIn'))}
          >
            <Checks width={16} height={16} color="#1f2937" />
            <span className="add-to-list-dropdown__section-title">
              Already In Lists
            </span>
          </div>
          <div
            {...accessibleOnClick(() => toggleSection('alreadyIn'))}
            className="add-to-list-dropdown__section-chevron"
          >
            {isExpanded ? (
              <ChevronUp width={16} height={16} color="#6B7280" />
            ) : (
              <ChevronDown width={16} height={16} color="#6B7280" />
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="add-to-list-dropdown__section-content">
            <div className="add-to-list-dropdown__section-divider" />
            <div className="add-to-list-dropdown__list">
              {sectionLists?.map((list) => (
                <div
                  key={list.id}
                  className="add-to-list-dropdown__list-item add-to-list-dropdown__list-item--disabled add-to-list-dropdown__list-item--already-in"
                >
                  <Checkbox checked disabled />
                  <div className="add-to-list-dropdown__list-item-content">
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <TruncatedTextWithAutoTooltip
                        text={list.label}
                        className="add-to-list-dropdown__list-item-label"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getSectionIcon = (section: 'public' | 'private' | 'teams') => {
    switch (section) {
      case 'public':
        return <World width={16} height={16} color="#1f2937" />;
      case 'private':
        return <Lock width={16} height={16} color="#1f2937" />;
      case 'teams':
        return <Users width={16} height={16} color="#1f2937" />;
      default:
        return null;
    }
  };

  const renderSection = (
    section: 'public' | 'private' | 'teams',
    sectionLists: AddToListData[],
    title: string,
  ) => {
    if (sectionLists.length === 0) return null;

    const isExpanded = expandedSections[section];
    // const allSelected = isAllSelectedInSection(sectionLists);
    // const someSelected = isSomeSelectedInSection(sectionLists);

    return (
      <div key={section} className="add-to-list-dropdown__section">
        <div
          className={`add-to-list-dropdown__section-header ${
            isExpanded ? 'add-to-list-dropdown__section-header--expanded' : ''
          }`}
        >
          <div
            className="add-to-list-dropdown__section-header-left"
            {...accessibleOnClick(() => toggleSection(section))}
          >
            {/* <Checkbox
              checked={allSelected}
              intermediate={someSelected}
              onChange={(checked) => {
                handleSelectAllInSection(checked, sectionLists);
              }}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
              }}
            /> */}
            <span className="add-to-list-dropdown__section-icon">
              {getSectionIcon(section)}
            </span>
            <span className="add-to-list-dropdown__section-title">{title}</span>
          </div>
          <div
            {...accessibleOnClick(() => toggleSection(section))}
            className="add-to-list-dropdown__section-chevron"
          >
            {isExpanded ? (
              <ChevronUp width={16} height={16} color="#6B7280" />
            ) : (
              <ChevronDown width={16} height={16} color="#6B7280" />
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="add-to-list-dropdown__section-content">
            <div className="add-to-list-dropdown__section-divider" />
            <div className="add-to-list-dropdown__list">
              {sectionLists?.map((list) => (
                <div
                  key={list.id}
                  className="add-to-list-dropdown__list-item"
                  onMouseEnter={() => setHoveredListId(list?.id)}
                  onMouseLeave={() => setHoveredListId(null)}
                  {...accessibleOnClick(() => toggleListSelection(list?.id))}
                >
                  <Checkbox checked={selectedListIds?.includes(list?.id)} />
                  <div className="add-to-list-dropdown__list-item-content">
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <TruncatedTextWithAutoTooltip
                        text={list.label}
                        className="add-to-list-dropdown__list-item-label"
                      />
                    </div>
                    {hoveredListId === list?.id && section !== 'private' && (
                      <Avatar
                        firstName={list.owner.firstName}
                        lastName={list.owner.lastName}
                        className="add-to-list-dropdown__list-item-avatar"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const modalFooterContent = (
    <div className="add-to-list-dropdown__modal-footer">
      <div
        className="add-to-list-dropdown__create-new"
        {...accessibleOnClick(handleOpenCreateModal)}
      >
        <Plus width={16} height={16} />
        <span className="add-to-list-dropdown__create-new-text">
          Create New List
        </span>
      </div>
      <div className="d-flex align-items-center gap-12 cursor-pointer">
        {(() => {
          // Avoid nested ternary expressions by using imperative logic to build the chip
          let chip: React.ReactNode = null;
          if (isCompanyTab) {
            if (shouldEnrich) {
              chip = (
                <OverlayTooltip
                  text="Saving to a list will enrich full company profile"
                  placement={Placement.Top}
                >
                  <div className="d-flex align-items-center gap-4 lead-credit-chip">
                    <Diamond width={14} height={14} color="#6B7280" />
                    <span className="lead-credit-chip__text">
                      {`${companyCredit}/ Company`}
                    </span>
                  </div>
                </OverlayTooltip>
              );
            }
          } else {
            chip = (
              <OverlayTooltip
                text={
                  peopleEnrichmentType === 'email_phone'
                    ? 'Saving to a list will enrich email and phone details for the selected lead(s).'
                    : 'Saving to a list will enrich email details for the selected lead(s).'
                }
                placement={Placement.Top}
                className="lead-credit-tooltip"
              >
                <div className="d-flex align-items-center gap-4 lead-credit-chip">
                  <Diamond width={14} height={14} color="#6B7280" />
                  <span className="lead-credit-chip__text">
                    {(() => {
                      const credit =
                        peopleEnrichmentType === 'email_phone'
                          ? emailPhoneCredit
                          : emailCredit;
                      return `${credit}/ Lead`;
                    })()}
                  </span>
                </div>
              </OverlayTooltip>
            );
          }
          return chip;
        })()}
        <Button
          onClick={handleAdd}
          className="add-to-list-dropdown__add-btn"
          disabled={isSaving}
        >
          {isSaving ? (
            <div className="d-flex align-items-center btn-loader">
              <Spinner animation={SpinnerAnimation.Border} />
              Saving
            </div>
          ) : (
            'Save'
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Button
        variant="secondary"
        disabled={disabled}
        className="btn-custom-secondary add-to-list-dropdown__toggle"
        onClick={() => {
          if (disabled) return;

          if (!isCompanyTab && !isPeopleSavedTab) {
            const effectiveCount =
              isSelectedAllLeads && peopleMaxLeadsLimit != null
                ? peopleMaxLeadsLimit
                : selectedResourcesCount;
            const revealType: LeadRevealType =
              // eslint-disable-next-line no-nested-ternary
              peopleEnrichmentType === 'email'
                ? isPersonalEmailMode
                  ? LeadRevealType.PersonalEmail
                  : LeadRevealType.Email
                : isPersonalEmailMode
                ? LeadRevealType.EmailPersonalEmailPhone
                : LeadRevealType.EmailPhone;

            const costPerLead =
              peopleEnrichmentType === 'email_phone'
                ? Number(emailPhoneCredit ?? 0)
                : Number(emailCredit ?? 0);
            const required = Number(effectiveCount) * costPerLead;
            if (Number(availableCredits) < required) {
              dispatch(openCreditNotAvailableModal(revealType));
              window.analytics?.track?.({
                userId: getUserTrackingId(),
                event: AnalyticsEvents.TrippedFeature,
                properties: {
                  shAccountId: getUserShAccountId(),
                  current_plan: getUserPlanName(),
                  role: getUserRole(),
                  'LF Plan': getUserLeadFinderPlanName(),
                  feature_type: 'Lead Finder',
                },
              });
              return;
            }
          }

          setShow(true);
        }}
      >
        <AddList width={16} height={16} />
        <span>Save to List</span>
        {/* <ChevronDown width={16} height={16} /> */}
      </Button>

      <Modal
        show={show}
        onClose={handleClose}
        onCancel={handleClose}
        onHide={handleClose}
        className="add-to-list-dropdown add-to-list-dropdown--modal"
        titleContent={
          <div className="d-flex modal-header-title">
            <div className="">Save to List</div>
          </div>
        }
        showCloseIcon
        hideCancelButton
        hideSubmitButton
        footerContent={modalFooterContent}
        backdrop="static"
        extraModalProps={{
          enforceFocus: false,
          'aria-labelledby': 'add-to-list-modal-title',
          onExited: () => {
            if (shouldOpenCreateModalAfterCloseRef.current) {
              shouldOpenCreateModalAfterCloseRef.current = false;
              setShowCreateModal(true);
            }
          },
        }}
      >
        <div className="add-to-list-dropdown__modal-body">
          <div className="transfer-container">
            <div className="transfer-body">
              <div className="transfer-search">
                <Input
                  placeholder="Search"
                  icons={[
                    {
                      place: Input.IconPlace.Left,
                      identifier: 'search',
                    },
                  ]}
                  value={searchValue}
                  onChange={setSearchValue}
                  className="transfer-search-input"
                />
              </div>
              <div className="transfer-content">
                {renderAlreadyInSection(groupedLists.alreadyIn)}
                {renderSection('public', groupedLists.public, 'Public')}
                {renderSection('private', groupedLists.private, 'Private')}
                {renderSection('teams', groupedLists.teams, 'Teams')}
              </div>
              {isCompanyTab && (
                <div className="add-to-list-dropdown__enrichment add-to-list-dropdown__enrichment--company">
                  <div className="add-to-list-dropdown__enrichment-company">
                    <Switch
                      checked={shouldEnrich}
                      onChange={(checked) => setShouldEnrich(checked)}
                    />
                    <span className="add-to-list-dropdown__enrichment-label">
                      Save with Full Profile Enrichment
                    </span>
                    <OverlayTooltip
                      text={
                        <div className="enrichment-tooltip-text">
                          <span className="enrichment-tooltip-title">
                            Full Enrichment Profile includes:
                          </span>
                          <span className="enrichment-tooltip-list">
                            Revenue, Employee Count, Technologies, Funding,
                            Social Profile, Industry Details
                          </span>
                        </div>
                      }
                      placement={Placement.Bottom}
                      className="enrichment-tooltip"
                    >
                      <InfoCircle
                        width={16}
                        height={16}
                        color="#6B7280"
                        className="cursor-pointer"
                      />
                    </OverlayTooltip>
                  </div>
                </div>
              )}
              {!isCompanyTab && (
                <div className="add-to-list-dropdown__enrichment add-to-list-dropdown__enrichment--people">
                  <div className="add-to-list-dropdown__enrichment-people">
                    <div className="add-to-list-dropdown__enrichment-company">
                      <Switch
                        checked={peopleEnrichmentType === 'email_phone'}
                        onChange={(checked) =>
                          setPeopleEnrichmentType(
                            checked ? 'email_phone' : 'email',
                          )
                        }
                      />
                      <span className="add-to-list-dropdown__enrichment-label">
                        {isPersonalEmailMode
                          ? 'Save Lead with Personal Email + Work Email + Phone Enrichment'
                          : 'Save Lead with Email + Phone Enrichment'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <CreateEditSavedListModal
        show={showCreateModal}
        onHide={() => {
          shouldOpenAddToListAfterCreateCloseRef.current = true;
          setShowCreateModal(false);
        }}
        showBackIcon={true}
        onBackClick={() => {
          shouldOpenAddToListAfterCreateCloseRef.current = true;
          setShowCreateModal(false);
        }}
        onExited={() => {
          if (shouldOpenAddToListAfterCreateCloseRef.current) {
            shouldOpenAddToListAfterCreateCloseRef.current = false;
            setShow(true);
          }
        }}
        onSave={(listId, listName, isShared, teamIds) => {
          if (onSaveList) {
            if (isCompanyTab) {
              onSaveList(listId, listName, isShared, teamIds, shouldEnrich);
            } else {
              let revealType: LeadRevealType;
              if (peopleEnrichmentType === 'email') {
                revealType = isPersonalEmailMode
                  ? LeadRevealType.PersonalEmail
                  : LeadRevealType.Email;
              } else {
                revealType = isPersonalEmailMode
                  ? LeadRevealType.EmailPersonalEmailPhone
                  : LeadRevealType.EmailPhone;
              }
              onSaveList(
                listId,
                listName,
                isShared,
                teamIds,
                undefined,
                revealType,
              );
            }
          }
          setShowCreateModal(false);
        }}
        isLoading={isUpdateListLoading}
        teamsList={teamsList}
        getTeamsListRequestStatus={getTeamsListRequestStatus}
        sendGetTeamsListRequest={sendGetTeamsListRequest}
        existingLists={lists}
        isCreateMode={true}
        primaryButtonLabel={
          isUpdateListLoading ? 'Saving...' : 'Create & Save to List'
        }
      />
    </>
  );
};

export default AddToListDropdown;
